import {
  ThreadFragment,
  useAppendMeetingStepDataMutation,
  useCircleThreadsSubscription,
  useThreadsWithMeetingNoteSubscription,
} from '@gql'
import useCurrentMember from '@/member/hooks/useCurrentMember'
import { useOrgContext } from '@/org/contexts/OrgContext'
import { DragDropContext, DropResult } from '@hello-pangea/dnd'
import { MeetingStepThreadsFragment } from '@rolebase/shared/model/meeting_step'
import {
  MeetingThreadsSortMode,
  ThreadWeather,
  ThreadsWeather,
} from '@rolebase/shared/model/threadWeather'
import { shuffleArray } from '@utils/shuffleArray'
import isEqual from 'lodash.isequal'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { MeetingContext } from '../contexts/MeetingContext'
import { CircleThreadWithMeetingNote } from './MeetingStepContentThreadItem'
import {
  collectAgendaIds,
  resolveStepThreads,
} from './meetingStepThreadsResolution'
import { getPickerCirclesIds } from './meetingThreadsPickerScope'
import { isThreadsStep } from './meetingStepThreadTypes'

const emptyArray: any[] = []

// The round's drag zone (#89). Exported so the bar and this context name the
// same thing: a mismatch here would silently disable the drag, with no error.
export const MEETING_MEMBERS_DND_TYPE = 'meeting-members'
export const MEETING_MEMBERS_DROPPABLE_PREFIX = 'meeting-members-'

interface Props {
  children: React.ReactNode
  disabled?: boolean
}

export interface NotAllowedThread {
  id: string
  notAllowed: true
}

export type ThreadMaybeAllowed = CircleThreadWithMeetingNote | NotAllowedThread

type StepData = MeetingStepThreadsFragment['data']

export interface MeetingThreadsContextValue {
  // Selected threads by step, with meeting activity note
  threadsByStep: Record<string, ThreadMaybeAllowed[]>
  // Threads that are not selected in any step
  availableThreads: ThreadFragment[]
  loading: boolean
  error: Error | undefined
  add: (stepId: string, threadId: string) => void
  remove: (stepId: string, threadId: string) => void
  randomize: (stepId: string) => void
  // Classify one tension for THIS meeting only (REU89-D1). Any participant may
  // call it: the facilitator's hand is a usage, not a lock (REU89-D2).
  setThreadWeather: (
    stepId: string,
    threadId: string,
    weather: ThreadWeather
  ) => void
  // The reading order everyone sees, and the order of the round: both are
  // shared, both die with the meeting (REU89-D7, D11).
  setSortMode: (stepId: string, mode: MeetingThreadsSortMode) => void
  setMembersOrder: (stepId: string, membersIds: string[]) => void
  // What the round bar is CURRENTLY showing, inherited order included. The
  // first drag happens before anything was ever written, so the drop has no
  // saved order to move things in — this tells it what the eye sees.
  reportMembersOrder: (stepId: string, membersIds: string[]) => void
  // What the LIST is currently showing, settled tensions at the bottom
  // included. The agenda keeps its own order, so the two differ as soon as one
  // tension is settled — and a drop reads positions off the screen.
  reportThreadsOrder: (stepId: string, threadsIds: string[]) => void
  // The step's data AS THE SCREEN MUST SHOW IT: the server's version with what
  // was just written on top, until the echo confirms it (#106). Read this,
  // never `step.data`, or a gesture jumps back for 1 to 2 s.
  getStepData: (step: { id: string; data: StepData }) => StepData
}

export const MeetingThreadsContext = createContext<
  MeetingThreadsContextValue | undefined
>(undefined)

export default function MeetingThreadsDragDropContext({
  children,
  disabled,
}: Props) {
  const { meeting, circle, steps, editable } = useContext(MeetingContext)!
  const { orgData } = useOrgContext()
  const currentMember = useCurrentMember()
  const [appendMeetingStepData] = useAppendMeetingStepDataMutation()

  // Threads picker scope (REUN-02): a tension is proposable only if its
  // circle is both in the meeting's scope (the meeting's circle plus its
  // direct children only, never deeper descendants — decision 04/08/2026,
  // e834b3cc) AND among the circles the current member participates in
  // (OrgData.getParticipantCircles). Representation is kept as-is: Yocoop's
  // container circles have no direct members, only child roles, so requiring
  // a direct membership would make a circle's own tensions unreachable from
  // its own meeting. An unknown current member (loading, or no member) never
  // widens the scope — it yields an empty list.
  const circlesIds = useMemo(() => {
    if (!circle) return emptyArray as string[]
    const childrenIds = orgData
      ? orgData.childrenOf(circle.id).map((c) => c.id)
      : []
    const participantCirclesIds =
      orgData && currentMember
        ? orgData.getParticipantCircles(currentMember.id).map((c) => c.id)
        : []
    const scopedIds = getPickerCirclesIds(
      circle.id,
      childrenIds,
      participantCirclesIds
    )
    return scopedIds.length > 0 ? scopedIds : (emptyArray as string[])
  }, [circle, orgData, currentMember])

  // Every step that holds threads, tactical and governance alike. The test goes
  // through isThreadsStep rather than comparing to one type inline: this filter
  // builds the WHOLE drag & drop context, so missing a type here leaves that
  // step silently empty, with no error — the shape of the D1 and REUN-01 bugs.
  const threadSteps = useMemo(
    () =>
      steps?.filter(
        (s) =>
          isThreadsStep(s.type) &&
          // Check if step is active, because we can keep step data after step config deletion
          meeting?.stepsConfig.some((sc) => sc.id === s.stepConfigId)
      ),
    [steps, meeting]
  )

  // All threads ids used in steps. Dedup comes from the same module that
  // resolves them, so the two can't drift apart (coveredIds below compares
  // one against the other). Sorted to prevent from reloading when changing
  // order.
  const stepThreadsIds: string[] = useMemo(() => {
    if (!threadSteps) return emptyArray as string[]
    return collectAgendaIds(
      threadSteps.map((step) => ({
        id: step.id,
        threadsIds: (step as MeetingStepThreadsFragment).data.threadsIds,
      }))
    ).sort((a, b) => a.localeCompare(b))
  }, [threadSteps])

  // Which ids the current selected-threads result was computed for. Apollo
  // exposes no public way to read the variables behind a result
  // (result.variables is @internal), so we record them ourselves when data
  // lands. Without it, "not loaded yet" and "not allowed" look identical, and
  // the padlock flashes on a thread someone else just added to the agenda.
  //
  // The ref is written during render, which is only safe because this app
  // renders in the default, non-interruptible lane: no StrictMode, no
  // startTransition, no useDeferredValue anywhere in the webapp. Render and
  // commit form one synchronous task, so no subscription message can land
  // between the two and pair a result with the wrong id list. Introducing any
  // concurrent feature would break that silently — move this to a state set
  // in an effect if that day comes. Failing that, coveredIds below compares
  // the recorded list with the current one and falls back to null (pending),
  // which is the safe side.
  const stepThreadsIdsRef = useRef<string[]>(stepThreadsIds)
  stepThreadsIdsRef.current = stepThreadsIds
  const [loadedThreadsIds, setLoadedThreadsIds] = useState<string[] | null>(
    null
  )

  // Subscribe to selected threads
  const {
    data: selectedThreadsData,
    error,
    loading,
  } = useThreadsWithMeetingNoteSubscription({
    skip: !meeting || stepThreadsIds.length === 0,
    variables: {
      threadsIds: stepThreadsIds,
      meetingId: meeting?.id || '',
    },
    onData: () => setLoadedThreadsIds(stepThreadsIdsRef.current),
  })

  // Ids the current result covers, or null when it is stale (the agenda
  // changed and the subscription has not answered for the new list yet).
  const coveredIds = useMemo(() => {
    if (stepThreadsIds.length === 0) return new Set<string>()
    if (
      !loadedThreadsIds ||
      loadedThreadsIds.length !== stepThreadsIds.length
    ) {
      return null
    }
    const loaded = new Set(loadedThreadsIds)
    return stepThreadsIds.every((id) => loaded.has(id)) ? loaded : null
  }, [loadedThreadsIds, stepThreadsIds])

  // Threads ids by step id
  const [threadsByStep, setThreadsByStep] = useState<
    Record<string, ThreadMaybeAllowed[]>
  >({})

  // Subscribe to all threads of circle and its direct children (REUN-02:
  // narrowed to circlesIds above, so an empty intersection — normal case,
  // not an error — must also skip the subscription instead of querying an
  // empty circlesIds array).
  const { data: threadsData } = useCircleThreadsSubscription({
    skip: !circle || !editable || circlesIds.length === 0,
    variables: {
      circlesIds,
    },
  })
  const threads = threadsData?.thread || (emptyArray as ThreadFragment[])

  // Threads that are not selected in any step
  const [availableThreads, setAvailableThreads] = useState<ThreadFragment[]>([])

  // Keep the last rendered threadsByStep reachable from the effect below
  // without listing it as a dependency (it would re-run on its own output).
  // Declared before that effect so it is always refreshed first on a commit.
  const threadsByStepRef = useRef(threadsByStep)
  useEffect(() => {
    threadsByStepRef.current = threadsByStep
  }, [threadsByStep])

  useEffect(() => {
    if (!threadSteps || (stepThreadsIds.length !== 0 && !selectedThreadsData)) {
      return
    }

    // No rule lives here anymore: extract the inputs, call the pure module,
    // store its output. The rule itself is in meetingStepThreadsResolution,
    // where it is unit-tested.
    const { threadsByStep: nextThreadsByStep, keptIds } =
      resolveStepThreads<CircleThreadWithMeetingNote>({
        steps: threadSteps.map((step) => ({
          id: step.id,
          threadsIds: (step as MeetingStepThreadsFragment).data.threadsIds,
        })),
        withNoteById: new Map(
          (selectedThreadsData?.thread ?? []).map((thread) => [
            thread.id,
            thread,
          ])
        ),
        inScopeById: new Map(
          threads.map((thread) => [thread.id, { ...thread, activities: [] }])
        ),
        prevByStep: threadsByStepRef.current,
        coveredIds,
      })

    setThreadsByStep(nextThreadsByStep)
    setAvailableThreads(threads.filter((thread) => !keptIds.has(thread.id)))
  }, [threadSteps, threads, selectedThreadsData, coveredIds])

  // Write ONE first-level key of the step's data, never the whole field.
  //
  // 🔑 `_append` merges at the first level of a jsonb column (REU89-D3): the key
  // sent is rewritten whole, the others are left untouched. That is what keeps
  // a participant classifying a tension from wiping the agenda another one is
  // reordering — the silent loss #46 describes. There is no path operator in
  // Hasura, so a caller holding a map merges it itself before calling.
  //
  // What was sent is also held in a local layer until the server echoes it
  // (#106): the screen only redraws on the subscription's echo, 1 to 2 s
  // later, and an Apollo `optimisticResponse` cannot shorten that —
  // `useSubscription` never reads the normalised cache (tried and dropped on
  // 13/08/2026, see MeetingStepContentChecklist).
  const [pendingStepData, setPendingStepData] = useState<
    Record<string, Partial<StepData>>
  >({})
  const inflightWrites = useRef<Record<string, number>>({})

  // A refused write leaves the screen on what the server knows. Only the keys
  // still holding the value sent: a later gesture on the same key stays.
  const forgetSent = useCallback((stepId: string, data: Partial<StepData>) => {
    setPendingStepData((pending) => {
      const layer = { ...pending[stepId] } as Record<string, unknown>
      for (const [key, value] of Object.entries(data)) {
        if (layer[key] === value) delete layer[key]
      }
      return { ...pending, [stepId]: layer as Partial<StepData> }
    })
  }, [])

  const appendStepData = useCallback(
    (stepId: string, data: Partial<StepData>) => {
      setPendingStepData((pending) => ({
        ...pending,
        [stepId]: { ...pending[stepId], ...data },
      }))
      inflightWrites.current[stepId] = (inflightWrites.current[stepId] ?? 0) + 1
      appendMeetingStepData({ variables: { id: stepId, data } })
        .then(({ data: result }) => {
          // A permission refusal is silent: null, not an error.
          if (!result?.update_meeting_step_by_pk) forgetSent(stepId, data)
        })
        .catch(() => forgetSent(stepId, data))
        .finally(() => {
          inflightWrites.current[stepId]--
        })
    },
    [appendMeetingStepData, forgetSent]
  )

  // Each new version of the steps: a key the server now holds is confirmed and
  // leaves the layer. Once nothing is in flight for a step, its whole layer
  // goes — the server wins, including over another participant's gesture.
  useEffect(() => {
    if (!threadSteps) return
    setPendingStepData((pending) => {
      let changed = false
      const next: Record<string, Partial<StepData>> = {}
      for (const [stepId, layer] of Object.entries(pending)) {
        const step = threadSteps.find((s) => s.id === stepId) as
          | MeetingStepThreadsFragment
          | undefined
        if (!step || !inflightWrites.current[stepId]) {
          changed = true
          continue
        }
        const kept = Object.fromEntries(
          Object.entries(layer).filter(
            ([key, value]) =>
              !isEqual(value, step.data[key as keyof StepData])
          )
        )
        if (Object.keys(kept).length !== Object.keys(layer).length) {
          changed = true
        }
        if (Object.keys(kept).length !== 0) next[stepId] = kept
      }
      return changed ? next : pending
    })
  }, [threadSteps])

  const getStepData = useCallback(
    (step: { id: string; data: StepData }): StepData => {
      const layer = pendingStepData[step.id]
      return layer ? { ...step.data, ...layer } : step.data
    },
    [pendingStepData]
  )

  const update = useCallback(
    (stepId: string, threadsIds: string[]) => {
      appendStepData(stepId, { threadsIds })
    },
    [appendStepData]
  )

  const setThreadWeather = useCallback(
    (stepId: string, threadId: string, weather: ThreadWeather) => {
      const step = threadSteps?.find((s) => s.id === stepId) as
        | MeetingStepThreadsFragment
        | undefined
      // Built on what is displayed: two classifications in a row compose
      // instead of the second one erasing the first.
      const current: ThreadsWeather =
        (step && getStepData(step).threadsWeather) ?? {}
      appendStepData(stepId, {
        threadsWeather: { ...current, [threadId]: weather },
      })
    },
    [threadSteps, appendStepData, getStepData]
  )

  // A ref, not state: the bar reports on every render and this must never
  // trigger one of its own.
  const displayedMembersOrder = useRef<Record<string, string[]>>({})
  const reportMembersOrder = useCallback(
    (stepId: string, membersIds: string[]) => {
      displayedMembersOrder.current[stepId] = membersIds
    },
    []
  )

  const displayedThreadsOrder = useRef<Record<string, string[]>>({})
  const reportThreadsOrder = useCallback((stepId: string, ids: string[]) => {
    displayedThreadsOrder.current[stepId] = ids
  }, [])

  const setSortMode = useCallback(
    (stepId: string, mode: MeetingThreadsSortMode) => {
      appendStepData(stepId, { sortMode: mode })
    },
    [appendStepData]
  )

  const setMembersOrder = useCallback(
    (stepId: string, membersIds: string[]) => {
      appendStepData(stepId, { membersOrder: membersIds })
    },
    [appendStepData]
  )

  const change = useCallback(
    (stepId: string, selectedThreads: ThreadMaybeAllowed[]) => {
      setThreadsByStep((threadsByStep) => ({
        ...threadsByStep,
        [stepId]: selectedThreads,
      }))
      update(
        stepId,
        selectedThreads.map((thread) => thread.id)
      )
    },
    []
  )

  // The step's threads IN THE ORDER ONE SEES THEM.
  //
  // 🔑 A drop gives indexes read off the screen, and since #89 the screen no
  // longer matches the agenda: settled tensions sink to the bottom. Moving
  // things inside the agenda order with a screen index would grab the wrong
  // tension — silently, and only in meetings where something was settled.
  // Anything the screen never reported is appended rather than dropped.
  const displayedThreads = useCallback(
    (stepId: string): ThreadMaybeAllowed[] => {
      const threads = threadsByStep[stepId] || []
      const ids = displayedThreadsOrder.current[stepId]
      if (!ids) return threads.slice()
      const byId = new Map(threads.map((thread) => [thread.id, thread]))
      const list = ids
        .map((id) => byId.get(id))
        .filter((thread): thread is ThreadMaybeAllowed => thread !== undefined)
      const shown = new Set(list.map((thread) => thread.id))
      return [...list, ...threads.filter((thread) => !shown.has(thread.id))]
    },
    [threadsByStep]
  )

  // Drag and drop a thread in a step or across steps
  const handleDragEnd = useCallback(
    ({ source, destination, type }: DropResult) => {
      const sameStep = destination?.droppableId === source.droppableId
      const sameIndex = destination?.index === source.index
      if (!destination || (sameStep && sameIndex)) {
        return
      }
      // The round's order travels in the same context as the tensions, told
      // apart by the droppable TYPE: @hello-pangea/dnd already refuses a drop
      // across two types, so the two zones can never mix a member with a
      // tension (#89).
      if (type === MEETING_MEMBERS_DND_TYPE) {
        const stepId = destination.droppableId.slice(
          MEETING_MEMBERS_DROPPABLE_PREFIX.length
        )
        const step = threadSteps?.find((s) => s.id === stepId) as
          | MeetingStepThreadsFragment
          | undefined
        const current =
          (step && getStepData(step).membersOrder) ??
          displayedMembersOrder.current[stepId]
        if (!current) return
        const next = current.slice()
        const [moved] = next.splice(source.index, 1)
        next.splice(destination.index, 0, moved)
        setMembersOrder(stepId, next)
        return
      }
      const sourceThreads = displayedThreads(source.droppableId)
      const destinationThreads = sameStep
        ? sourceThreads
        : displayedThreads(destination.droppableId)
      const [removed] = sourceThreads.splice(source.index, 1)
      destinationThreads.splice(destination.index, 0, removed)
      change(destination.droppableId, destinationThreads)
      if (!sameStep) {
        change(source.droppableId, sourceThreads)
      }
    },
    [
      threadsByStep,
      threadSteps,
      setMembersOrder,
      displayedThreads,
      getStepData,
    ]
  )

  // Add a thread to a step
  const add = useCallback(
    (stepId: string, threadId: string) => {
      const stepThreads = threadsByStep[stepId] || []
      // Already on the agenda: adding it again would list it twice, here and
      // in the compte rendu (#132, seen on gouv 26/09)
      if (stepThreads.some((t) => t.id === threadId)) return
      const thread = threads.find((thread) => thread.id === threadId)
      if (!thread) {
        update(stepId, [...stepThreads.map((t) => t.id), threadId])
        return
      }
      const newThreads = [...stepThreads, { ...thread, activities: [] }]
      change(stepId, newThreads)
    },
    [threads, threadsByStep, change]
  )

  // Remove thread from step
  const remove = useCallback(
    (stepId: string, threadId: string) => {
      const newThreads = (threadsByStep[stepId] || []).filter(
        (thread) => thread.id !== threadId
      )
      change(stepId, newThreads)
    },
    [threadsByStep, change]
  )

  // Randomize threads order in a step
  const randomize = useCallback(
    (stepId: string) => {
      const stepThreads = threadsByStep[stepId]
      if (!stepThreads || stepThreads.length < 2) return
      change(stepId, shuffleArray(stepThreads))
    },
    [threadsByStep, change]
  )

  const value: MeetingThreadsContextValue = useMemo(() => {
    return {
      threadsByStep,
      availableThreads,
      loading,
      error,
      add,
      remove,
      randomize,
      setThreadWeather,
      setSortMode,
      setMembersOrder,
      reportMembersOrder,
      reportThreadsOrder,
      getStepData,
    }
  }, [
    threadsByStep,
    availableThreads,
    loading,
    error,
    add,
    remove,
    randomize,
    setThreadWeather,
    setSortMode,
    setMembersOrder,
    reportMembersOrder,
    reportThreadsOrder,
    getStepData,
  ])

  return (
    <MeetingThreadsContext.Provider value={value}>
      <DragDropContext
        key={disabled ? 0 : 1}
        enableDefaultSensors={!disabled}
        onDragEnd={handleDragEnd}
      >
        {children}
      </DragDropContext>
    </MeetingThreadsContext.Provider>
  )
}
